import clsx from 'clsx';
import type { ReactNode } from 'react';

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent';

const TONES: Record<Tone, string> = {
  neutral: 'bg-surface-3 text-muted',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
  accent: 'bg-accent-soft text-accent-soft-fg',
};

export function Badge({ tone = 'neutral', children, className }: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 px-2.5 py-[3px] text-[11px] font-semibold',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

const STATUS_TONES: Record<string, Tone> = {
  ACTIVE: 'success',
  CURRENT: 'accent',
  PLANNED: 'info',
  PENDING: 'info',
  DRAFT: 'neutral',
  SCHEDULED: 'info',
  ONGOING: 'accent',
  RESULTS_PROCESSING: 'warning',
  PUBLISHED: 'success',
  ARCHIVED: 'neutral',
  SUBMITTED: 'info',
  APPROVED: 'success',
  REJECTED: 'danger',
  GRADED: 'success',
  ABSENT: 'warning',
  EXEMPT: 'neutral',
  COMPLETED: 'info',
  GRADUATED: 'info',
  INACTIVE: 'neutral',
  CLOSED: 'neutral',
  TRANSFERRED: 'warning',
  LOCKED: 'danger',
  SUSPENDED: 'danger',
  WITHDRAWN: 'danger',
  VALIDATED: 'info',
  COMMITTED: 'success',
};

/**
 * A status pill.
 *
 * The dot is not decoration: it is the second channel that keeps the state
 * legible when the colour itself cannot be distinguished — colour-blindness,
 * greyscale printing, or forced-colors mode. The word carries the meaning; the
 * colour only reinforces it.
 */
export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'neutral';
  return (
    <Badge tone={tone}>
      <span className="h-1.5 w-1.5 bg-current" aria-hidden="true" />
      {status.replaceAll('_', ' ').toLowerCase()}
    </Badge>
  );
}
